import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Logo } from "../lib/brand";
import { api } from "../lib/api";
import { PasswordField } from "../components/PasswordField";
import { isStrongPassword, PASSWORD_REQUIREMENTS_MESSAGE, PASSWORD_MIN_LENGTH } from "../lib/password";
import { useI18n } from "../lib/i18n";

export default function ResetPassword() {
  const { t } = useI18n();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, navigate] = useLocation();

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("token") || "";
    setToken(t);
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!isStrongPassword(password)) { setError(PASSWORD_REQUIREMENTS_MESSAGE); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true);
    try {
      const r = await api.resetPassword(token, password);
      if (r?.ok) { setDone(true); setTimeout(() => navigate("/login"), 2500); }
      else setError((r as any)?.error || "This reset link is invalid or has expired.");
    } catch (err: any) { setError(err?.message || "This reset link is invalid or has expired."); }
    finally { setBusy(false); }
  }

  return (
    <div className="min-h-[80vh] grid place-items-center container-x py-10">
      <div className="card p-6 sm:p-8 max-w-md w-full">
        <div className="flex justify-center mb-4"><Logo withTagline /></div>
        <h1 className="text-2xl font-extrabold text-navy text-center mb-1">Set a new password</h1>

        {done ? (
          <div className="space-y-4 mt-4">
            <div className="rounded-lg bg-brand-green/10 text-brand-greendark text-sm p-4 text-center">
              Your password has been updated. Redirecting you to sign in…
            </div>
            <Link href="/login" className="btn-red w-full">Sign in now</Link>
          </div>
        ) : !token ? (
          <div className="space-y-4 mt-4">
            <p className="text-sm text-mut text-center">This link is missing its reset token. Request a new one below.</p>
            <Link href="/forgot-password" className="btn-red w-full">Request a reset link</Link>
          </div>
        ) : (
          <form className="space-y-3 mt-4" onSubmit={submit}>
            <div><label className="label">New password</label>
              <PasswordField id="reset-password" required minLength={PASSWORD_MIN_LENGTH} autoComplete="new-password" placeholder="Create a password" value={password} onChange={setPassword} showRequirements />
            </div>
            <div><label className="label">Confirm new password</label>
              <PasswordField id="reset-password-confirm" required minLength={PASSWORD_MIN_LENGTH} autoComplete="new-password" placeholder="Re-enter password" value={confirm} onChange={setConfirm} />
            </div>
            {error && <p className="text-sm text-brand-red">{error}</p>}
            <button type="submit" disabled={busy} className="btn-red w-full disabled:opacity-60">{busy ? "Updating…" : t("auth.resetPassword.submit", "Update password")}</button>
            <p className="text-center text-sm text-mut"><Link href="/login" className="text-brand-red font-semibold">Back to sign in</Link></p>
          </form>
        )}
      </div>
    </div>
  );
}
